import { useRouter } from "@tanstack/react-router";
import { ChevronDown, Inbox, Plus } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { CursorPagination } from "@/components/shared/pagination";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useStaffingRequestPriorities, useStaffingRequests } from "@/hooks/use-staffing-requests";
import { useSession } from "@/lib/auth-client";
import { hasCapability } from "@/lib/permissions";
import {
  STAFFING_REQUEST_KINDS,
  STAFFING_REQUEST_STATUSES,
  type StaffingRequestKind,
  type StaffingRequestStatus,
} from "@/schemas/staffing-requests";
import { NewStaffingRequestDialog, type SwapPrefill } from "./NewStaffingRequestDialog";
import { PlansToStaff } from "./PlansToStaff";
import { RequestQueueRow } from "./RequestQueueRow";
import { KIND_LABELS } from "./request-copy";

const PAGE_SIZE = 25;

type StatusFilter = "all" | StaffingRequestStatus;

export function RequestQueue() {
  const router = useRouter();
  const { data: session } = useSession();
  const canRequest = hasCapability(session?.user?.role, "requestStaffingChange");
  const [status, setStatus] = useState<StatusFilter>("Pending");
  const [kinds, setKinds] = useState<StaffingRequestKind[]>([]);
  const [priorityIds, setPriorityIds] = useState<string[]>([]);
  const { data: priorities = [] } = useStaffingRequestPriorities();
  const [formOpen, setFormOpen] = useState(false);
  // Set when the form is opened from an open role, so it starts as a pre-filled Swap.
  const [prefill, setPrefill] = useState<SwapPrefill | null>(null);

  function openForm(next: SwapPrefill | null) {
    setPrefill(next);
    setFormOpen(true);
  }
  // Cursor pagination state. Reset to page 1 whenever a filter changes.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError, error, refetch } = useStaffingRequests({
    statuses: status === "all" ? undefined : [status],
    kinds: kinds.length > 0 ? kinds : undefined,
    priorityIds: priorityIds.length > 0 ? priorityIds : undefined,
    cursor,
    direction,
    limit: PAGE_SIZE,
  });

  function resetPaging() {
    setCursor(undefined);
    setDirection("next");
    setPage(1);
  }

  function selectStatus(next: StatusFilter) {
    setStatus(next);
    resetPaging();
  }

  function toggleKind(kind: StaffingRequestKind) {
    setKinds((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));
    resetPaging();
  }

  function togglePriority(id: string) {
    setPriorityIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
    resetPaging();
  }

  function clearPriorities() {
    setPriorityIds([]);
    resetPaging();
  }

  function goNext() {
    if (!data?.nextCursor) return;
    setCursor(data.nextCursor);
    setDirection("next");
    setPage((p) => p + 1);
  }

  function goPrev() {
    if (!data?.prevCursor) return;
    setCursor(data.prevCursor);
    setDirection("prev");
    setPage((p) => Math.max(1, p - 1));
  }

  function openRequest(id: string) {
    router.navigate({ to: "/dashboard/staffing-requests/$id", params: { id } });
  }

  const rows = data?.data ?? [];
  const filtered = kinds.length > 0 || priorityIds.length > 0;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Manage"
        title="Staffing Requests"
        description="Ask Resource Management to add, replace, remove or swap someone on a Solution's Pod."
        actions={
          canRequest ? (
            <Button onClick={() => openForm(null)}>
              <Plus className="mr-1.5 h-4 w-4" />
              New request
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm uppercase tracking-wide text-muted-foreground">Status</span>
        {STAFFING_REQUEST_STATUSES.map((s) => (
          <FilterChip key={s} label={s} active={status === s} onClick={() => selectStatus(s)} />
        ))}
        <FilterChip label="All" active={status === "all"} onClick={() => selectStatus("all")} />
        <span className="ml-2 text-sm uppercase tracking-wide text-muted-foreground">Kind</span>
        {STAFFING_REQUEST_KINDS.map((k) => (
          <FilterChip
            key={k}
            label={KIND_LABELS[k]}
            active={kinds.includes(k)}
            onClick={() => toggleKind(k)}
          />
        ))}
        {priorities.length > 0 && (
          <>
            <span className="ml-2 text-sm uppercase tracking-wide text-muted-foreground">
              Priority
            </span>
            <MultiSelectFilter
              idPrefix="queue-priority"
              anyLabel="Any priority"
              options={priorities}
              selectedIds={priorityIds}
              onToggle={togglePriority}
              onClear={clearPriorities}
            />
          </>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load staffing requests."}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading &&
        !isError &&
        data !== undefined &&
        (rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No requests"
            description={
              filtered
                ? "No requests match the selected kind or priority."
                : status === "Pending"
                  ? "Nothing waiting on a decision."
                  : status === "all"
                    ? "No staffing requests yet."
                    : `No ${status.toLowerCase()} requests.`
            }
            action={
              canRequest ? (
                <Button variant="outline" onClick={() => openForm(null)}>
                  New request
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-md border border-border bg-card">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Request</th>
                    <th className="px-3 py-2 font-semibold">Solution</th>
                    <th className="px-3 py-2 font-semibold">Proposed Resource</th>
                    <th className="px-3 py-2 font-semibold">Priority</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 font-semibold">Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <RequestQueueRow key={row.id} request={row} onOpen={openRequest} />
                  ))}
                </tbody>
              </table>
            </div>

            <CursorPagination
              total={data.total}
              page={page}
              limit={PAGE_SIZE}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onNext={goNext}
              onPrev={goPrev}
              loading={isFetching}
              itemLabel="requests"
            />
          </div>
        ))}

      <PlansToStaff onSwap={openForm} />

      {canRequest && (
        <NewStaffingRequestDialog open={formOpen} onOpenChange={setFormOpen} initial={prefill} />
      )}
    </PageContainer>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "inline-flex items-center rounded-full bg-foreground px-2.5 py-0.5 text-sm font-medium text-background transition"
          : "inline-flex items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-sm font-medium text-muted-foreground transition hover:border-foreground/40 hover:text-foreground"
      }
    >
      {label}
    </button>
  );
}

/**
 * A popover checklist filter, so the toolbar stays one compact control per facet no matter
 * how many options are configured. Matches requests with ANY selected option.
 */
function MultiSelectFilter({
  idPrefix,
  anyLabel,
  options,
  selectedIds,
  onToggle,
  onClear,
}: {
  idPrefix: string;
  anyLabel: string;
  options: Array<{ id: string; name: string; active: boolean }>;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const count = selectedIds.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={
            count > 0
              ? "inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-0.5 text-sm font-medium text-background transition"
              : "inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-0.5 text-sm font-medium text-muted-foreground transition hover:border-foreground/40 hover:text-foreground"
          }
        >
          {count > 0 ? `${count} selected` : anyLabel}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 gap-2 p-3">
        {options.map((o) => (
          <div key={o.id} className="flex items-center gap-2 text-sm">
            <Checkbox
              id={`${idPrefix}-${o.id}`}
              checked={selectedIds.includes(o.id)}
              onCheckedChange={() => onToggle(o.id)}
            />
            <label htmlFor={`${idPrefix}-${o.id}`} className="cursor-pointer">
              {o.name}
              {!o.active && <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>}
            </label>
          </div>
        ))}
        {count > 0 && (
          <Button variant="ghost" size="sm" className="mt-1 justify-start px-0" onClick={onClear}>
            Clear selection
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
